import { CircleCheck, CircleDashed, CircleMinus } from "lucide-react";
import { PopoverHeading, Status, StatusPopover, formatDate } from "@amber/ui";
import { CHECK_RESULT_LABELS, type Qualification } from "../contracts/index";

const RESULT_ICON = {
  passed: <CircleCheck size={13} aria-hidden="true" className="text-done" />,
  not_yet: <CircleDashed size={13} aria-hidden="true" className="text-muted" />,
  not_applicable: <CircleMinus size={13} aria-hidden="true" className="text-muted" />,
} as const;

function Notes({ title, lines }: { title: string; lines: readonly string[] }) {
  if (lines.length === 0) return null;
  return (
    <>
      <dt>{title}</dt>
      <dd>{lines.join(" · ")}</dd>
    </>
  );
}

export function RouteQualification({ name, q }: { name: string; q: Qualification }) {
  const applicable = q.checks.filter((c) => c.result !== "not_applicable");
  const notApplicable = q.checks.filter((c) => c.result === "not_applicable");
  return (
    <StatusPopover
      label={<Status tone={q.level === "production" ? "ok" : "off"}>{q.label}</Status>}
      accessibleLabel={`${name}: ${q.label}, onboarding record`}
    >
      <div
        tabIndex={0}
        role="region"
        aria-label={`${name} onboarding record`}
        className="-mx-1 max-h-[calc(var(--radix-popover-content-available-height)-26px)] overflow-y-auto px-1"
      >
        <PopoverHeading>{q.label}</PopoverHeading>
        <p>{q.basis}</p>
        <ul className="m-0 mb-1.5 flex list-none flex-col gap-1 p-0 text-md">
          {applicable.map((c) => (
            <li key={c.check} className="grid grid-cols-[16px_1fr] gap-x-1.5">
              <span className="pt-0.5">{RESULT_ICON[c.result]}</span>
              <span>
                <span className="font-strong text-fg">{c.label}</span>
                <span className="text-muted"> · {CHECK_RESULT_LABELS[c.result]}</span>
                <span className="block text-muted">{c.evidence}</span>
              </span>
            </li>
          ))}
          {notApplicable.length > 0 ? (
            <li className="grid grid-cols-[16px_1fr] gap-x-1.5 text-muted">
              <span className="pt-0.5">{RESULT_ICON.not_applicable}</span>
              <span>
                {CHECK_RESULT_LABELS.not_applicable}: {notApplicable.map((c) => c.label).join(", ")}
              </span>
            </li>
          ) : null}
        </ul>
        <dl>
          <dt>Licence</dt>
          <dd>{q.licence.summary}</dd>
          <Notes title="Unknown" lines={q.licence.unknowns} />
          <Notes title="Restricted" lines={q.licence.restrictions} />
          <Notes title="Unsupported" lines={q.unsupported} />
          <Notes title="History" lines={q.historical_limits ? [q.historical_limits] : []} />
          <dt>Assessed</dt>
          <dd>{formatDate(`${q.assessed_on}T12:00:00Z`)}</dd>
        </dl>
      </div>
    </StatusPopover>
  );
}
